import { createPublicClient, http, type Chain, type HttpTransportConfig, type Transport } from "viem";
import { getRPCSettings, defaultRPCSettings } from "@/constants/chain-specific/chains";
import type { ChainType } from "@/constants/chain-specific";
import { chainRpcConfigs } from "@/config/index";
import { createRateLimitedTransport, globalRateLimiter } from "./rate-limited-transport";

/**
 * Viem Batching Configuration Guide
 *
 * This file provides optimized configurations for Viem's HTTP batch JSON-RPC
 * to handle rate-limited blockchain RPCs effectively.
 */

/**
 * Creates a rate-limited public client with proper configuration for both client and server-side
 * Uses the global rate limiter to ensure consistent rate limiting across all clients
 */
export const createRateLimitedClient = (
  chain: Chain,
  chainId?: ChainType,
  options: {
    bypassForNFTMetadata?: boolean;
    customRpcUrl?: string;
  } = {}
) => {
  const actualChainId = chainId ?? chain.id;
  const rpcConfig = chainRpcConfigs[actualChainId];
  const rpcSettings = getRPCSettings(actualChainId);

  // Use custom RPC URL if provided, otherwise use configured RPC
  const rpcUrl = options.customRpcUrl || rpcConfig?.primaryRpcUrl;

  if (!rpcUrl) {
    console.warn(`No RPC URL found for chain ${chain.name} (${actualChainId})`);
    return null;
  }

  return createPublicClient({
    chain,
    transport: createRateLimitedTransport(rpcUrl, {
      maxTokens: rpcSettings.maxTokens,
      refillRate: rpcSettings.refillRate,
      batch: {
        batchSize: rpcSettings.batchSize,
        wait: rpcSettings.batchWait,
      },
      bypassForNFTMetadata: options.bypassForNFTMetadata,
    }),
  });
};

/**
 * Creates a public client with chain-specific RPC rate limiting settings
 * Uses the global rate limiter for consistent rate limiting
 */
export const createBatchedPublicClient = (
  chain: Chain,
  rpcUrl?: string,
  chainId?: ChainType
) => {
  const settings = getRPCSettings(chainId ?? chain.id);
  const actualChainId = chainId ?? chain.id;

  const transport = http(rpcUrl, {
    batch: {
      batchSize: defaultRPCSettings.batchSize!,
      wait: defaultRPCSettings.batchInterval!,
    },
    retryCount: defaultRPCSettings.maxRetries!,
    retryDelay: defaultRPCSettings.retryDelay!,
    timeout: defaultRPCSettings.retryDelay! * defaultRPCSettings.maxRetries!,
    fetchOptions: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
    async onRequest(params: { method: string; url: string; body?: any }) {
      // Use global rate limiter
      await globalRateLimiter.waitForToken();
      const status = globalRateLimiter.getStatus();
      console.log(`🌐 Global RPC request (${status.tokens}/${status.maxTokens} tokens)`);
      return params;
    }
  } as HttpTransportConfig);

  return createPublicClient({
    chain,
    transport
  });
};

/**
 * Batching Best Practices:
 *
 * 1. **Use appropriate batch sizes**:
 *    - Public RPCs: 20-50 requests per batch
 *    - Premium RPCs: 100-500 requests per batch
 *    - NFT metadata: 5-20 requests per batch
 *
 * 2. **Set appropriate wait times**:
 *    - Longer waits (100-200ms) allow more requests to accumulate
 *    - Shorter waits (50ms) provide faster response for small batches
 *
 * 3. **Handle failures gracefully**:
 *    - Always use `allowFailure: true` with `useReadContracts`
 *    - Check `result.status` before accessing `result.result`
 *
 * 4. **Monitor and adjust**:
 *    - Log batch sizes and success rates
 *    - Adjust parameters based on RPC performance
 *    - Consider circuit breakers for persistent failures
 *
 * Chain-specific settings are now defined in chain configuration:
 * - Shibarium: Higher limits (batchSize: 25, interval: 300ms)
 * - Other chains: Default limits (batchSize: 40, interval: 300ms)
 *
 * Rate limiting is implemented using a token bucket algorithm:
 * - Each chain has its own rate limiter instance
 * - Tokens are refilled at a configurable rate
 * - Requests wait if no tokens are available
 */

// Common batch configurations for specific use cases
export const BATCH_CONFIGS = {
  NFT: { batchSize: 5, wait: 200 }, // Conservative for NFT metadata
  PRICE: { batchSize: 20, wait: 50 }, // Aggressive for price updates
  BALANCE: { batchSize: 10, wait: 100 }, // Moderate for balance checks
} as const;

/**
 * Usage Examples:
 *
 * // Use with existing hooks:
 * import { useReadContracts } from "wagmi";
 * import { createBatchedPublicClient, BATCH_CONFIGS } from "@/util/viem-batching";
 *
 * const { data } = useReadContracts({
 *   contracts: contractCalls,
 *   allowFailure: true,
 *   batchSize: BATCH_CONFIGS.CONSERVATIVE.batchSize, // 50 requests per batch
 * });
 *
 * // Create custom client:
 * const client = createBatchedPublicClient(chain, undefined, 50, 100);
 */